import { Fragment, useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch, connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIList from 'UIComponents/list/UIList';
import UILink from 'UIComponents/link/UILink';
import H2 from 'UIComponents/elements/headings/H2';
import FollowupEmailSelect from 'ContentComponents/cmv2/form/formAutomation/followupEmail/FollowupEmailSelect';
import WorkflowLink from 'ContentComponents/cmv2/form/formAutomation/connectedWorkflows/WorkflowLink';
import EmailEditorPanel from 'ContentComponents/cmv2/form/formAutomation/followupEmail/emailEditor/EmailEditorPanel';
import * as workflowsSelectors from 'ContentData/selectors/workflows';
import * as emailsSelectors from 'ContentData/selectors/emails';
import PickersContext from 'ContentComponents/context/PickersContext';
import { fetchEmailBuffer } from 'ContentData/actions/Emails';
import text from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = (state, props) => {
  const {
    formGuid,
    contentId
  } = props;
  return {
    workflowsThatSendEmails: workflowsSelectors.getWorkflowsThatSendEmails(state, {
      formGuid,
      contentId
    })
  };
};
const connector = connect(mapStateToProps);
const i18nKey = key => `ContentComponents.cmv2.fields.form.formWorkflowDependencies.followupEmailWorkflows.${key}`;
const FollowupEmailWorkflowModal = ({
  emailId: initialEmailId,
  formGuid,
  hasAutomatedEmailEditorAccess,
  hasInlineEmailEditorAccess,
  hasWorkflowsAppAccess,
  onClose,
  onConfirm,
  workflowsThatSendEmails
}) => {
  const dispatch = useDispatch();
  const [emailId, setEmailId] = useState(initialEmailId);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [isCreatingEmail, setIsCreatingEmail] = useState(false);
  const emailBuffers = useSelector(state => emailsSelectors.getEmailBuffers(state));
  useEffect(() => {}, [emailBuffers]);
  const emailBuffer = emailId && emailBuffers[emailId];
  const isInlineEditable = emailBuffer && emailBuffer.canBeEditedInEmbeddedEditor;
  const showUnpublishedError = useSelector(state => Boolean(emailId && emailsSelectors.getIsEmailUnpublished(state, {
    id: emailId
  })));
  const onCloseEmailEditor = newEmailId => {
    if (newEmailId && newEmailId !== emailId) {
      setEmailId(newEmailId);
    }
    setShowEmailEditor(false);
    setIsCreatingEmail(false);
  };
  const onSelectEmail = newEmailId => {
    setEmailId(newEmailId);
    if (newEmailId && !emailBuffers[newEmailId]) {
      dispatch(fetchEmailBuffer(newEmailId)).catch(err => {
        throw err;
      });
    }
  };
  const pickerComponents = useContext(PickersContext);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [showEmailEditor && /*#__PURE__*/_jsx(EmailEditorPanel, {
      emailId: !isCreatingEmail ? emailId : undefined,
      hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
      onClose: onCloseEmailEditor,
      pickerComponents: pickerComponents
    }), /*#__PURE__*/_jsxs(UIModal, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onClose
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.header')
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [workflowsThatSendEmails.length > 0 && /*#__PURE__*/_jsxs(UIAlert, {
          "data-selenium": "form-followup-email-workflows-sending-emails-alert",
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.workflowsThatSendEmailsAlert.title')
          }),
          type: "warning",
          children: [/*#__PURE__*/_jsx(Text, {
            marginBlockEnd: "200",
            as: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.workflowsThatSendEmailsAlert.body1')
            })
          }), /*#__PURE__*/_jsx(UIList, {
            className: "m-bottom-2",
            styled: true,
            children: workflowsThatSendEmails.map(workflow => {
              return /*#__PURE__*/_jsx(WorkflowLink, {
                formGuid: formGuid,
                formWorkflowDependency: workflow,
                hasWorkflowsAppAccess: hasWorkflowsAppAccess
              }, workflow.flowId);
            })
          }), /*#__PURE__*/_jsx(Text, {
            margin: "0",
            as: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.workflowsThatSendEmailsAlert.body2')
            })
          })]
        }), /*#__PURE__*/_jsx(UIFormControl, {
          error: showUnpublishedError,
          help: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: i18nKey('modal.helpText_jsx'),
            options: {
              href: 'https://knowledge.hubspot.com/email/create-automated-emails-to-use-in-workflows'
            },
            elements: {
              Link: UILink
            }
          }),
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.emailSelect.label')
          }),
          required: true,
          validationMessage: showUnpublishedError && /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.unpublishedEmailError')
          }),
          children: /*#__PURE__*/_jsx(FollowupEmailSelect, {
            error: showUnpublishedError,
            hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
            hasInlineEmailEditorAccess: hasInlineEmailEditorAccess,
            onChange: onSelectEmail,
            onCreateEmail: () => {
              setShowEmailEditor(true);
              setIsCreatingEmail(true);
            },
            placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.emailSelect.placeholder')
            }),
            value: emailId
          })
        }), hasInlineEmailEditorAccess && isInlineEditable && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(UIButton, {
            "data-selenium": "form-followup-email-inline-edit-button",
            onClick: () => setShowEmailEditor(true),
            size: "small",
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.editEmail')
            })
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          disabled: !emailId || showUnpublishedError,
          onClick: () => onConfirm(emailId),
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.save')
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.cancel')
          })
        })]
      })]
    })]
  });
};
const FollowupEmailWorkflowButton = ({
  followupEmailId,
  formGuid,
  hasAutomatedEmailEditorAccess,
  hasInlineEmailEditorAccess,
  hasWorkflowsAppAccess,
  onChangeFollowupEmailEvent,
  onFollowupEmailIdChange,
  workflowsThatSendEmails,
  readOnly = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isEditing = !!followupEmailId;
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openModal = () => {
    setIsModalOpen(true);
  };
  const onConfirm = newEmailId => {
    if (isEditing && newEmailId === followupEmailId) {
      closeModal();
      return;
    }
    if (onChangeFollowupEmailEvent) {
      onChangeFollowupEmailEvent(isEditing ? 'form-followup-email-edit' : 'form-followup-email-add');
    }
    onFollowupEmailIdChange(newEmailId);
    closeModal();
    return;
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [isModalOpen && /*#__PURE__*/_jsx(FollowupEmailWorkflowModal, {
      emailId: followupEmailId,
      formGuid: formGuid,
      hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
      hasInlineEmailEditorAccess: hasInlineEmailEditorAccess,
      hasWorkflowsAppAccess: hasWorkflowsAppAccess,
      onClose: closeModal,
      onConfirm: onConfirm,
      workflowsThatSendEmails: workflowsThatSendEmails
    }), isEditing ? /*#__PURE__*/_jsx(UIIconButton, {
      "aria-label": text(i18nKey('editEmailTooltip')),
      disabled: readOnly,
      onClick: openModal,
      size: "xs",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('editEmailTooltip')
      }),
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "edit"
      })
    }) : /*#__PURE__*/_jsxs(UIIconButton, {
      disabled: readOnly,
      onClick: openModal,
      size: "xs",
      use: "link",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('buttonLabel')
      }), /*#__PURE__*/_jsx(UIIcon, {
        className: "p-left-1",
        name: "add"
      })]
    })]
  });
};
export default connector(FollowupEmailWorkflowButton);